/**
 * Générateur — Il associe écriture décimale, écriture fractionnaire et notation
 * scientifique (nombres décimaux).
 * Attendu ID : 137 | Niveau : 4ème | Domaine : Nombres (desc 11, O2)
 *
 * Source doc prof (Cluster 1, O2) : a × 10ⁿ → écriture décimale, écriture
 * scientifique (mantisse + exposant), « lesquels sont en notation scientifique »,
 * QCM d'écriture scientifique. Plan de cartes validé 2026-07-14 :
 *
 *   Carte « decimale » — a × 10ⁿ → écriture décimale (qcm_unique), n ∈ [-7 ; 7]
 *   Carte « scientifique » — nombre → mantisse + exposant EN DEUX TROUS (saisie)
 *     le doc l'impose explicitement (« évite les litiges de saisie ») ;
 *     mantisse POSITIVE (la définition affichée dit 1 ≤ a < 10), n ∈ [-7 ; 7] sans 0.
 *   Carte « estsci » — est-ce écrit en notation scientifique ? (tableau_vf)
 *     2 à 4 lignes vraies sur 6 ; fausses : mantisse ≥ 10, mantisse < 1 (avec remise).
 *   Carte « ecriture » — l'écriture scientifique de N est… (qcm_unique)
 *     distracteur clé = même valeur, mantisse non normalisée ; N donné une fois
 *     sur deux en écriture fractionnaire (783/1 000 000), n ∈ [-7 ; 7] sans 0.
 *
 * Revue 2026-09-22 : deux défauts où une réponse juste était comptée fausse,
 * la mantisse NÉGATIVE attendue dans « scientifique » (un item sur trois) et
 * dans les lignes vraies de « estsci » (une sur deux) alors que l'énoncé affiche
 * 1 ≤ a < 10 : signe supprimé ; ligne « π × 10⁴ » retirée (π vérifie la
 * condition affichée, rien n'écrit que la mantisse doit être décimale) ;
 * tableau à nombre de vraies variable, familles fausses tirées avec remise ;
 * « decimale » [-4 ; 4] → [-7 ; 7], plus de zéro final dans la mantisse
 * (« 123,10 ») ; « ecriture » [2 ; 8] → [-7 ; 7] sans 0, nombre donné en
 * fraction décimale une fois sur deux (jamais réduite : 654/10, c'est
 * l'écriture qui se lit directement) ; « scientifique » [-6 ; 8] → [-7 ; 7]
 * sans 0 (« 3,2 = 3,2 × 10⁰ » ne travaille rien) ; parenthèses de consigne →
 * phrases, la mantisse n'est plus annoncée « à virgule » (elle est entière
 * dans un tiers des cas).
 *
 * Revue 2026-09-23 : tout ce qui s'affiche comme puissance passe en KaTeX
 * (règle de la maison, décision prof) : tex de generators/utils.js remplace
 * puiss de nombres.js (exposants Unicode) et les <sup> des énoncés, dans les
 * énoncés, les choix du QCM « ecriture » et les lignes du tableau ; les
 * lettres a et n en KaTeX ; la phrase à trous de « scientifique » passe en
 * mode mathématique global (ligne entière entre dollars, sans mot français,
 * exposant en trou protégé par un groupe, 10^{{{e}}}). Les réponses (mantisse
 * et exposant saisis) ne changent pas.
 *
 * Composition 2026-09-23 (règle prof, tableaux vrai-faux) : « estsci » passe
 * de six lignes à quatre, sinon c'est trop long à lire ; une à trois vraies,
 * donc toujours une vraie et une fausse ; les quatre exposants restent
 * distincts et les mantisses tirées, la variété ne bouge pas.
 *
 * Ajout 2026-09-23 : cinquième carte « associer », la chaîne à trois maillons
 * de l'intitulé (fraction = décimale = scientifique), qui n'existait nulle
 * part : trois cartes sur quatre portent sur la seule notation scientifique et
 * l'écriture fractionnaire n'apparaît que dans « ecriture », une fois sur deux
 * (constat prof). Modèle : l'exemple de réussite officiel, tel quel,
 * « 783 / 1 000 000 = 0,000783 = 7,83 × 10⁻⁴ ». Phrase à trous en saisie, une
 * seule ligne entièrement en mode mathématique, en environnement aligned de
 * KaTeX pour que les deux signes égal soient l'un sous l'autre (correction
 * prof le jour même : sur deux lignes séparées, le second « = » restait collé
 * à la marge sous un « = » au milieu ; un \hphantom en tête de seconde ligne
 * est compté par le filet de l'impression, écarté) ; le registre donné est tiré
 * au sort, les deux autres sont en trous ; quand la fraction est cherchée,
 * seul son dénominateur est en trou, le numérateur se lit déjà dans les autres
 * écritures et une fraction entière en saisie admettrait plusieurs écritures
 * justes. Les quatre cartes existantes restent telles quelles, « estsci »
 * comprise (décision prof).
 *
 * Tout par construction ; développement décimal EXACT (nombres.js, aucun
 * flottant). Puissances en KaTeX (affichage), réponses signées ASCII « - ».
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeVraiFaux, fracTex, fracTrouTex, tex,
} from '../../../utils';
import { developpe, dec10 } from './nombres';

const ATTENDU_ID = 137;

const L_DECIMALE = 'Passer de a × 10ⁿ à l\'écriture décimale';
const L_SCI = 'Écrire un nombre en notation scientifique';
const L_ESTSCI = 'Reconnaître une notation scientifique';
const L_ECRITURE = 'Choisir l\'écriture scientifique d\'un nombre';
const L_ASSOCIER = 'Associer écriture fractionnaire, écriture décimale et notation scientifique';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const grp = (s) => {
    const neg = s.startsWith('-');
    const x = neg ? s.slice(1) : s;
    const [ent, dec] = x.split(',');
    const g = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (neg ? '-' : '') + (dec !== undefined ? `${g},${dec}` : g);
};

// Entier NON NUL de [min ; max] (min < 0 < max), sans boucle : on tire dans
// [min ; max - 1] et on décale d'un cran tout ce qui est ≥ 0.
const expNonNul = (min, max) => { const x = randInt(min, max - 1); return x >= 0 ? x + 1 : x; };

// Mantisse aléatoire POSITIVE à 1-3 chiffres significatifs, normalisée (1 ≤ m < 10).
const mantisseNormale = () => {
    const ent = randInt(1, 9);
    const nd = randInt(0, 2);
    let dec = '';
    for (let i = 0; i < nd; i++) dec += String(randInt(0, 9));
    dec = dec.replace(/0+$/, '');
    return dec ? `${ent},${dec}` : String(ent);
};

// Mantisse normalisée AVEC une ou deux décimales, dernier chiffre non nul
// (sans boucle : les distracteurs « mantisse entière » et « < 1 » de la carte
// « ecriture » dégénéreraient sur une mantisse entière).
const mantisseAVirgule = () => {
    const ent = randInt(1, 9);
    return randomChoice([false, true]) ? `${ent},${randInt(1, 9)}` : `${ent},${randInt(0, 9)}${randInt(1, 9)}`;
};

// Écriture fractionnaire (fraction décimale) d'une écriture décimale à virgule :
// « 0,00654 » → 654 / 100 000, « 65,4 » → 654 / 10. Jamais réduite : c'est
// l'écriture qui se lit directement sur le nombre.
const fractionDecimale = (brut) => {
    const [ent, dec] = brut.split(',');
    return fracTex(parseInt(ent + dec, 10), 10 ** dec.length);
};

// Décimal français en LaTeX : « 6,54 » → « 6{,}54 » (sinon KaTeX espace la
// virgule). À appeler sur une écriture SANS séparateur de milliers : les
// espaces sont ignorés en mode mathématique, les milliers sont posés au rendu
// (utils/milliers.js, espacerMilliersTex).
const nbTex = (s) => String(s).replace(',', '{,}');
// « a × 10ⁿ » en KaTeX : sciTex('6,54', -3) → $6{,}54 \times 10^{-3}$.
const sciTex = (mant, e) => tex(`${nbTex(mant)} \\times 10^{${e}}`);

// ═════════ Carte « decimale » — a × 10ⁿ → écriture décimale ═════════

const genDecimaleQcm = () => {
    // Mantisse quelconque (pas forcément normalisée) : entier 1-999, 0 à 2
    // décimales, dernier chiffre décimal jamais nul (pas de « 123,10 »).
    const ent = randInt(1, 999);
    const nd = randInt(0, 2);
    let mant = String(ent);
    if (nd === 1) mant += `,${randInt(1, 9)}`;
    if (nd === 2) mant += `,${randInt(0, 9)}${randInt(1, 9)}`;
    const n = randInt(-7, 7);
    const correct = grp(developpe(mant, n));
    const cand = new Set([correct]);
    [n + 1, n - 1, -n, n + 2].forEach(m => cand.add(grp(developpe(mant, m))));
    const distracteurs = [...cand].filter(c => c !== correct);
    const choix = [correct, ...shuffle(distracteurs).slice(0, 3)];
    const payload = makeQcm(choix, correct,
        `<p><b>Quelle est l'écriture décimale de ${sciTex(mant, n)} ?</b></p>`, opts(L_DECIMALE));
    payload._meta.params = { mant, n };
    return payload;
};

// ═════════ Carte « scientifique » — mantisse + exposant en deux trous ═════════

const genScientifiqueTrous = () => {
    const mant = mantisseNormale();          // positive : l'énoncé affiche 1 ≤ a < 10
    const e = expNonNul(-7, 7);
    const brut = developpe(mant, e);
    const plain = grp(brut);
    // Ligne entière en mode mathématique (dollars aux deux bouts, cf.
    // hasMathLines) : l'exposant en trou est protégé par un groupe, 10^{{{e}}}
    // et jamais 10^{{e}} (KaTeX refuse un ^ suivi du markup du trou).
    const phrase = `$${nbTex(brut)} = {{${mant}}} \\times 10^{{{${e}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On écrit ${plain} en notation scientifique, c'est-à-dire sous la forme ${tex('a \\times 10^{n}')} avec ${tex('1 \\leq a \\lt 10')}.</p>`
        + `<p><b>Complète la mantisse ${tex('a')} et l'exposant ${tex('n')}.</b></p>`
        + '<p>La mantisse s\'écrit avec une virgule quand elle n\'est pas entière, par exemple 3,2. '
        + 'Un exposant négatif s\'écrit avec le signe « - », par exemple -3.</p>',
        { ...opts(L_SCI), mode: 'saisie' });
    payload._meta.params = { mant, e, plain };
    return payload;
};

// ═════════ Carte « estsci » — est-ce une notation scientifique ? ═════════

// Mantisse ≥ 10 : « 42 » ou « 42,7 » (jamais de zéro final).
const mantisseGrande = () => `${randInt(10, 99)}${randomChoice(['', `,${randInt(1, 9)}`])}`;
// Mantisse < 1 : « 0,3 », « 0,35 », « 0,05 » (jamais de zéro final).
const mantissePetite = () => randomChoice([
    `0,${randInt(1, 9)}`,
    `0,${randInt(0, 9)}${randInt(1, 9)}`,
]);
const POOL_EXPOS_ESTSCI = [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
// Quatre lignes (règle prof 2026-09-23 : quatre au plus, sinon trop long à lire).
const NB_LIGNES_ESTSCI = 4;

const genEstSciTableau = () => {
    // 1 à 3 lignes vraies sur 4, donc toujours au moins une vraie et une fausse
    // (mantisse normalisée POSITIVE, la définition affichée dit 1 ≤ a < 10) ;
    // les fausses sont tirées AVEC REMISE parmi les deux familles restantes,
    // mantisse ≥ 10 et mantisse < 1. Les quatre exposants sont distincts :
    // quatre lignes distinctes par construction, sans boucle.
    const nbVrai = randInt(1, NB_LIGNES_ESTSCI - 1);
    const familles = [];
    for (let i = 0; i < nbVrai; i++) familles.push('normale');
    for (let i = nbVrai; i < NB_LIGNES_ESTSCI; i++) familles.push(randomChoice(['grande', 'petite']));
    const expos = shuffle(POOL_EXPOS_ESTSCI).slice(0, NB_LIGNES_ESTSCI);
    const lignes = [];
    const bonnes = [];
    shuffle(familles).forEach((f, i) => {
        const mant = f === 'normale' ? mantisseNormale() : (f === 'grande' ? mantisseGrande() : mantissePetite());
        lignes.push(sciTex(mant, expos[i]));
        bonnes.push(f === 'normale' ? 0 : 1);
    });
    const payload = makeVraiFaux(lignes, bonnes,
        `<p>Un nombre est écrit en notation scientifique quand il est de la forme ${tex('a \\times 10^{n}')} avec ${tex('1 \\leq a \\lt 10')}.</p>`
        + '<p><b>Pour chaque nombre, indique s\'il est écrit en notation scientifique.</b></p>', opts(L_ESTSCI));
    payload._meta.params = { lignes, bonnes, nbVrai };
    return payload;
};

// ═════════ Carte « ecriture » — l'écriture scientifique de N est… ═════════

const genEcritureQcm = () => {
    const mant = mantisseAVirgule();
    const e = expNonNul(-7, 7);              // e ≠ 0 : l'exposant opposé serait la clé
    const brut = developpe(mant, e);
    const plain = grp(brut);
    const correct = sciTex(mant, e);
    const digits = mant.replace(',', '');                 // « 654 »
    const dec = mant.split(',')[1].length;                // nb de décimales
    const cand = new Set([correct]);
    cand.add(sciTex(digits, e - dec));                                  // même valeur, mantisse ≥ 10
    cand.add(sciTex(`0,${digits}`, e - dec + digits.length));           // même valeur, mantisse < 1
    cand.add(sciTex(mant, -e));                                         // exposant opposé (valeur ≠, e ≠ 0)
    const distracteurs = [...cand].filter(c => c !== correct);
    const choix = [correct, ...shuffle(distracteurs).slice(0, 3)];
    // Le nombre est donné une fois sur deux en écriture fractionnaire (fraction
    // décimale), quand il a une partie décimale ; sinon en écriture décimale.
    const enFraction = randomChoice([false, true]) && brut.includes(',');
    const affiche = enFraction ? fractionDecimale(brut) : plain;
    const payload = makeQcm(choix, correct,
        `<p><b>Quelle est l'écriture scientifique de ${affiche} ?</b></p>`, opts(L_ECRITURE));
    payload._meta.params = { mant, e, plain, enFraction };
    return payload;
};

// ═════════ Carte « associer » — fraction = décimale = scientifique ═════════

// Mantisse normalisée (1 ≤ a < 10) à une, deux ou trois décimales, dernière
// décimale non nulle : « 7,83 », « 7,03 », jamais « 7,80 » (deux écritures
// d'un même nombre). Toujours à virgule, sans boucle.
const mantisseDecimale = () => {
    const nd = randInt(1, 3);
    let dec = '';
    for (let i = 1; i < nd; i++) dec += String(randInt(0, 9));
    dec += String(randInt(1, 9));
    return `${randInt(1, 9)},${dec}`;
};

// Exposant de la chaîne : [-7 ; 7] sans 0, la plage des autres cartes, mais un
// exposant positif ne dépasse pas nd - 1 (nd = nombre de décimales de la
// mantisse), sinon le nombre est entier (7,83 × 10⁴ = 78 300) et n'a plus
// d'écriture fractionnaire à lire. Tirage uniforme parmi les exposants permis.
const exposantChaine = (nd) => {
    const permis = [-7, -6, -5, -4, -3, -2, -1];
    for (let k = 1; k < nd; k++) permis.push(k);
    return randomChoice(permis);
};

// « a × 10ⁿ » SANS les dollars, à insérer dans une ligne de phrase à trous
// déjà entourée de $…$ (pendant de fracTrouTex) : sciTrouTex('7,83', -4) →
// 7{,}83 \times 10^{-4}. L'exposant négatif est groupé entre accolades.
const sciTrouTex = (mant, e) => `${nbTex(mant)} \\times 10^{${e}}`;

const genAssocierTrous = () => {
    const mant = mantisseDecimale();                  // « 7,83 »
    const nd = mant.length - 2;                       // ses décimales : 2
    const e = exposantChaine(nd);                     // -4
    const brut = developpe(mant, e);                  // « 0,000783 », exact
    const dec = brut.split(',')[1];                   // « 000783 »
    const num = mant.replace(',', '');                // « 783 » : les chiffres de la mantisse
    const den = dec10(dec.length);                    // « 1000000 » : une puissance de dix, chaîne
    // Le registre DONNÉ est tiré au sort, les deux autres sont en trous : la
    // carte travaille l'association dans les trois sens et pas une seule
    // direction. Quand la fraction est cherchée, seul son dénominateur est
    // en trou : le numérateur se lit déjà dans les autres écritures, et une
    // fraction entière en saisie admettrait plusieurs écritures justes.
    const depart = randomChoice(['fraction', 'decimale', 'scientifique']);
    // Une SEULE ligne, entièrement en mode mathématique (hasMathLines), en
    // environnement aligned de KaTeX : les deux signes égal sont l'un sous
    // l'autre (correction prof 2026-09-23 ; sur deux lignes séparées le second
    // « = » restait collé à la marge, et un \hphantom en tête de seconde ligne
    // est compté par le filet de l'impression, texVisible). Le trou en exposant
    // porte son groupe protégé, 10^{{{e}}} et jamais 10^{{e}} (motif gen_205) ;
    // la virgule affichée s'écrit {,}, les mots attendus gardent la virgule
    // ordinaire (l'élève tape au clavier). Le registre donné ouvre la chaîne,
    // l'écriture décimale, pivot entre les deux autres, vient au plus près.
    // La ligne entière reste sous les 60 caractères visibles du filet
    // (Print/helpers/expressions.js).
    const fracDonnee = fracTrouTex(num, den);
    const fracTrou = fracTrouTex(num, `{{${den}}}`);
    const sciDonnee = sciTrouTex(mant, e);
    const sciTrous = `{{${mant}}} \\times 10^{{{${e}}}}`;
    const decDonnee = nbTex(brut);
    const decTrou = `{{${brut}}}`;
    const MEMBRES = {
        fraction: [fracDonnee, decTrou, sciTrous],
        decimale: [decDonnee, fracTrou, sciTrous],
        scientifique: [sciDonnee, decTrou, fracTrou],
    };
    const COMPLETE = {
        fraction: 'l\'écriture décimale et la notation scientifique',
        decimale: 'l\'écriture fractionnaire et la notation scientifique',
        scientifique: 'l\'écriture décimale et l\'écriture fractionnaire',
    };
    const [premier, deuxieme, troisieme] = MEMBRES[depart];
    // Quatre barres dans le gabarit JavaScript pour la double barre de LaTeX.
    const phrase = `$\\begin{aligned}${premier} &= ${deuxieme} \\\\ &= ${troisieme}\\end{aligned}$`;
    const payload = makeTexteTrou(phrase,
        '<p>Écriture fractionnaire, écriture décimale et notation scientifique : '
        + 'les trois écritures désignent le même nombre.</p>'
        + `<p><b>Complète ${COMPLETE[depart]}.</b></p>`,
        { ...opts(L_ASSOCIER), mode: 'saisie' });
    payload._meta.params = { depart, mant, e, brut, num, den };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    decimale_qcm_unique: genDecimaleQcm,
    scientifique_texte_trou_saisie: genScientifiqueTrous,
    estsci_tableau_vf: genEstSciTableau,
    ecriture_qcm_unique: genEcritureQcm,
    associer_texte_trou_saisie: genAssocierTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'decimale', label: L_DECIMALE, formatsList: buildFormats('decimale') },
    { id: 'scientifique', label: L_SCI, formatsList: buildFormats('scientifique') },
    { id: 'estsci', label: L_ESTSCI, formatsList: buildFormats('estsci') },
    { id: 'ecriture', label: L_ECRITURE, formatsList: buildFormats('ecriture') },
    { id: 'associer', label: L_ASSOCIER, formatsList: buildFormats('associer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
